import Tree, { TreeNode } from 'rc-tree';
import React from 'react';
import { Icon, Numbers } from '@weezevent/nacre';
import './dragDropRates.css';
import useAppContext from '../../../context/useAppContext';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

const findObject = (data, key, callback) => {
    for (let i = 0; i < data.length; i++) {
        if (data[i].key === key) {
            return callback(data[i], i, data);
        }

        if (data[i].children) {
            findObject(data[i].children, key, callback);
        }
    }
};

function Header() {
    const { t } = useTranslation();

    return (
        <div className={'drag-drop-rates-header'}>
            <div className={'drag-drop-rates-header-rate'}>{t('ticket.rates.sortRates.columnRates')}</div>
            <div className={'drag-drop-rates-header-price'}>{t('ticket.common.price')}</div>
        </div>
    );
}

const TitleItem = React.memo(({ item }) => {
    const [{ event }] = useAppContext();
    const { t } = useTranslation();

    return (
        <div className={'title-item-container'}>
            <div className={'title-item'}>{item.title}</div>
            {!item.group && item.price && (
                <div className={'title-price'}>
                    {item.price !== '0.00' ? <Numbers value={item.price} locale={i18n.language} currency={event.currency} showNullDecimal /> : t('ticket.rates.free')}
                </div>
            )}
        </div>
    );
});

function DropIndicator({ dropPosition, dropLevelOffset, indent }) {
    const style = {
        pointerEvents: 'none',
        position: 'absolute',
        right: 0,
        backgroundColor: '#0032fa',
        height: 2,
        left: dropPosition ? -dropLevelOffset * indent : indent,
        bottom: dropPosition >= 0 ? 0 : undefined,
        top: dropPosition < 0 ? 0 : undefined
    };
    return <div style={style} />;
}

export default function DragdropRates({ tree, onChangeTree }) {
    const allowDrop = React.useCallback(
        ({ dragNode, dropNode, dropPosition }) => {
            //dropPosition can take 3 values (-1,0,1)
            // -1 => above the dropNode
            // 0 => in the dropNode
            // 1 => below the dropNode

            //do not allow to put an item in a rate
            if (!dropNode.group) {
                if (dropPosition === 0) return false;
            }

            // we prohibit putting a rate under a group
            if (!dragNode.group && dropNode.group && dropPosition === 1) {
                return false;
            }

            if (dragNode.group) {
                //we do not allow to put a group above a rate that has no group
                if (!dropNode.group && !dropNode.parent) {
                    if (dropPosition === -1) {
                        return false;
                    }
                    let countRatesWithoutParent = tree.filter(i => !i.group && !i.parent).length;
                    let dropIndex = tree.findIndex(i => i.key === Number(dropNode.key));
                    if (dropIndex < countRatesWithoutParent - 1) {
                        return false;
                    }
                }

                //we do not allow to put a group above a rate which itself is already in a group
                if (!dropNode.group && dropNode.parent) {
                    let dropParentObj = null;
                    findObject(tree, Number(dropNode.parent), item => {
                        dropParentObj = item;
                    });

                    if (dropParentObj) {
                        let dropParentRatesArray = dropParentObj.children.filter(i => !i.group);
                        let countRatesWithoutParent = dropParentRatesArray.length;
                        let dropIndex = dropParentRatesArray.findIndex(i => i.key === Number(dropNode.key));
                        if (dropIndex < countRatesWithoutParent - 1) {
                            return false;
                        }
                    }
                }

                if (dropNode.group && dropPosition === 0) {
                    //you cannot put a group as the first child of a parent group if it already has child rates
                    let dropObj = null;
                    findObject(tree, Number(dropNode.key), item => {
                        dropObj = item;
                    });

                    if (dropObj) {
                        let countRates = dropObj.children.filter(i => !i.group).length;
                        if (countRates > 0) {
                            return false;
                        }
                    }
                }

                // not being able to put a group in a group, if the group to be moved already has subgroups
                if ((dropNode.group && dropPosition === 0) || (!dropNode.group && dropNode.parent)) {
                    let dragObj = null;
                    findObject(tree, Number(dragNode.key), item => {
                        dragObj = item;
                    });

                    if (dragObj) {
                        let countGroups = dragObj.children.filter(i => i.group).length;
                        if (countGroups > 0) {
                            return false;
                        }
                    }
                }

                //does not allow to put a group in a subgroup
                if (dropNode.group && dropNode.parent && dropPosition === 0) {
                    return false;
                }

                //does not allow to put a group in a subgroup, same here but if the group is dropped next to a rate which itself is already in a subgroup
                if (!dropNode.group && dropNode.parent && Number(dropNode.higherParent) !== Number(dropNode.parent)) {
                    return false;
                }
            }

            return true;
        },
        [tree]
    );

    const onDrop = React.useCallback(
        info => {
            const dropKey = Number(info.node.key);
            const dragKey = Number(info.dragNode.key);
            const dropPos = info.node.pos.split('-');
            const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);

            const data = [...tree];

            // Find dragObject
            let dragObj = null;
            findObject(data, dragKey, (item, index, arr) => {
                // remove dragObject from its old location
                arr.splice(index, 1);
                dragObj = item;
            });

            // Drop directly on the targeted parent group
            if (!info.dropToGap) {
                // Find dropObject
                findObject(data, dropKey, _dropObj => {
                    _dropObj.children = _dropObj.children || [];
                    dragObj.parent = _dropObj.group ? _dropObj.key : _dropObj.parent; //Update parent dragObject
                    _dropObj.children.unshift(dragObj); //insert first child in DropObject
                });
            } else {
                let children = [];
                let i = 0;
                let dropObj = null;
                // Find dropObject
                findObject(data, dropKey, (_dropObj, index, _children) => {
                    // Here 'children' can be either the list of elements without parents if the drop was made at the highest level,
                    // or the list of children of the group in which the drop was made
                    children = _children;
                    i = index;
                    dropObj = _dropObj;
                });
                dragObj.parent = dropObj.parent || null; //Update parent dragObject

                children.splice(dropPosition === -1 ? i : i + 1, 0, dragObj); //Update location dragObj
            }

            onChangeTree(data);
        },
        [tree]
    );

    return (
        <>
            <Header />
            <Tree
                autoExpandParent={false}
                defaultExpandParent={false}
                allowDrop={allowDrop}
                icon={() => false}
                className="draggable-tree"
                draggable={{ icon: <Icon className={'icon-drag'} name={'menu-burger'} /> }}
                onDrop={onDrop}
                switcherIcon={props => props.group && <Icon className={'icon-folder'} name={props.expanded ? 'folder-open' : 'folder-closed'} />}
                defaultExpandAll={false}
                dropIndicatorRender={({ dropPosition, dropLevelOffset, indent }) => <DropIndicator dropPosition={dropPosition} dropLevelOffset={dropLevelOffset} indent={indent} />}
            >
                {tree.map(item => {
                    if (item.group && item.children) {
                        return (
                            <TreeNode
                                title={() => <TitleItem item={item} />}
                                key={item.key}
                                group={true}
                                higherParent={null}
                                parent={item.parent}
                                className={item.parent ? 'title-subgroup' : 'title-group'}
                            >
                                {item.children.map(child => {
                                    if (child.group && child.children) {
                                        return (
                                            <TreeNode
                                                title={() => <TitleItem item={child} />}
                                                key={child.key}
                                                group={true}
                                                higherParent={item.key}
                                                parent={child.parent}
                                                className={child.parent ? 'title-subgroup' : 'title-group'}
                                            >
                                                {child.children.map(rate => {
                                                    return (
                                                        <TreeNode
                                                            title={() => <TitleItem item={rate} />}
                                                            group={false}
                                                            key={rate.key}
                                                            higherParent={item.key}
                                                            parent={rate.parent}
                                                            className={'title-rate'}
                                                        />
                                                    );
                                                })}
                                            </TreeNode>
                                        );
                                    }
                                    return (
                                        <TreeNode
                                            title={() => <TitleItem item={child} />}
                                            group={false}
                                            key={child.key}
                                            higherParent={item.key}
                                            parent={child.parent}
                                            className={'title-rate'}
                                        />
                                    );
                                })}
                            </TreeNode>
                        );
                    }
                    return <TreeNode title={() => <TitleItem item={item} />} group={false} key={item.key} className={'title-rate'} parent={item.parent} />;
                })}
            </Tree>
        </>
    );
}
